import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';

const ScratchCard = ({ prize, onComplete }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isRevealed, setIsRevealed] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [scratchedPercentage, setScratchedPercentage] = useState(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    
    // Set canvas size to match container
    const resizeCanvas = () => {
      if (containerRef.current) {
        canvas.width = containerRef.current.offsetWidth;
        canvas.height = containerRef.current.offsetHeight;
        fillCanvas(ctx, canvas);
      }
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    return () => window.removeEventListener('resize', resizeCanvas);
  }, []);

  const fillCanvas = (ctx, canvas) => {
    // Draw silver coating
    ctx.fillStyle = '#C0C0C0';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add some noise/texture to look like a scratch card
    for (let i = 0; i < 500; i++) {
      ctx.fillStyle = Math.random() > 0.5 ? '#A9A9A9' : '#D3D3D3';
      ctx.fillRect(
        Math.random() * canvas.width,
        Math.random() * canvas.height,
        2, 2
      );
    }
    
    // Instructions text
    ctx.fillStyle = '#666';
    ctx.font = 'bold 24px Noto Sans JP';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('ここをこすってね！', canvas.width / 2, canvas.height / 2);
  };

  const calculateScratchedArea = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    
    let transparentPixels = 0;
    // Check every 4th pixel (alpha channel) to optimize
    for (let i = 3; i < pixels.length; i += 16) {
      if (pixels[i] < 128) {
        transparentPixels++;
      }
    }
    
    const totalPixelsToCheck = pixels.length / 16;
    const percentage = (transparentPixels / totalPixelsToCheck) * 100;
    
    setScratchedPercentage(percentage);
    
    if (percentage > 50 && !isRevealed) {
      revealAll();
    }
  };

  const revealAll = () => {
    setIsRevealed(true);
    const canvas = canvasRef.current;
    
    // Fade out canvas
    canvas.style.transition = 'opacity 0.5s ease-out';
    canvas.style.opacity = '0';
    
    setTimeout(() => {
      onComplete();
    }, 1000);
  };

  const handlePointerDown = (e) => {
    if (isRevealed) return;
    setIsDrawing(true);
    scratch(e);
  };

  const handlePointerMove = (e) => {
    if (!isDrawing || isRevealed) return;
    scratch(e);
  };

  const handlePointerUp = () => {
    setIsDrawing(false);
    if (!isRevealed) {
      calculateScratchedArea();
    }
  };

  const scratch = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Support both mouse and touch
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, 25, 0, Math.PI * 2);
    ctx.fill();
  };

  return (
    <div className="relative w-full max-w-sm aspect-video mx-auto" ref={containerRef}>
      {/* Background Prize Layer */}
      <div className="absolute inset-0 bg-white rounded-xl shadow-inner flex flex-col items-center justify-center p-4 border-4 border-remo-accent">
        {prize ? (
          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: isRevealed ? 1 : 0.8, opacity: isRevealed ? 1 : 0.5 }}
            className="text-center"
          >
            <div className="text-4xl mb-2">{prize.icon || '🎁'}</div>
            <div className="text-remo-sub font-bold text-xl">{prize.label}</div>
            <div className="text-remo-main font-black text-2xl mt-1">{prize.points} pt</div>
          </motion.div>
        ) : (
          <div className="text-gray-400">Loading...</div>
        )}
      </div>

      {/* Scratch Layer */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full rounded-xl cursor-crosshair touch-none"
        onMouseDown={handlePointerDown}
        onMouseMove={handlePointerMove}
        onMouseUp={handlePointerUp}
        onMouseLeave={handlePointerUp}
        onTouchStart={handlePointerDown}
        onTouchMove={handlePointerMove}
        onTouchEnd={handlePointerUp}
      />
    </div>
  );
};

export default ScratchCard;